import {ReactNode} from 'react';

import {assert} from 'Asserts';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataType, DataTypeToTSType, DeprecatedDataFrame} from 'data-frames/DataFrameTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {Analysis} from 'ui-library/charting/statistics/Types';
import {CartesianPoint, Dataset} from 'ui-library/charting/Types';

type OutlierConfig = NonNullable<
  ExtractByTypename<
    AnalyticsVisualization['parameters'],
    'AnalyticsVisualizationScatterPlotParametersNode'
  >['outlierConfig']
>;

// Keep in sync with benchling/insights/statistics/regression/regression_config_spec.py
export enum RegressionType {
  LINEAR = 'LINEAR',
  LOGISTIC = 'LOGISTIC',
  QUADRATIC = 'QUADRATIC',
}

export enum RegressionParameterName {
  MIN = 'MIN',
  MAX = 'MAX',
  IC50 = 'IC50',
  HILL_SLOPE = 'HILL_SLOPE',
}

export enum RegressionParameterConstraintType {
  NONE = 'NONE',
  EQUAL = 'EQUAL',
  LESS_THAN = 'LESS_THAN',
  GREATER_THAN = 'GREATER_THAN',
  BETWEEN = 'BETWEEN',
}

export type RegressionConfigParameterConstraint =
  | {type: RegressionParameterConstraintType.NONE}
  | {type: RegressionParameterConstraintType.EQUAL; value: number}
  | {type: RegressionParameterConstraintType.LESS_THAN; upperBound: number}
  | {type: RegressionParameterConstraintType.GREATER_THAN; lowerBound: number}
  | {type: RegressionParameterConstraintType.BETWEEN; lowerBound: number; upperBound: number};

export interface RegressionParameterCustomization {
  name: RegressionParameterName;
  initialValue: number | null;
  constraint: RegressionConfigParameterConstraint;
}

// Keep in sync with regression_config_spec.py REGRESSION_CONFIG_SPEC_SHAPE
export interface RegressionConfig {
  regressionType: RegressionType;
  parameterCustomizations: Array<RegressionParameterCustomization>;
  version: 1;
}

export type RegressionAnalysis<T = unknown> = Analysis<RegressionAnalysisResult<T>>;

export interface RegressionAnalysisResult<TFitOutput = unknown> {
  getEstimatedY: (x: RegressionXAxisTSTypes) => number;
  fitEquation: string | null;
  outputValues: Array<{
    fieldName: string;
    fieldNameDisplayOverride?: string;
    value: number;
    showInPlotHeader: boolean;
  }>;
  fitOutput: TFitOutput;
  tooltipDisclaimer: ReactNode;
  detectedOutliers?: Set<string>;
}

export interface RegressionAxisTypes {
  x: DataType.DATE_OR_TIMESTAMP | DataType.NUMBER | DataType.STRING;
  y: DataType.NUMBER;
}
type DataTypeToRegressionTSType = Omit<DataTypeToTSType, DataType.DATE_OR_TIMESTAMP> & {
  [DataType.DATE_OR_TIMESTAMP]: Date;
};
export type RegressionXAxisTSTypes = DataTypeToRegressionTSType[RegressionAxisTypes['x']];

export type RegressionDataset<TDataPoint extends CartesianPoint = CartesianPoint> =
  DeprecatedDataFrame<TDataPoint>;

// TODO(BNCH-24546) Unify dataset definitions with definitions in client/ui-library/charting/Types.ts
export type NumericRegressionDataset = RegressionDataset<
  CartesianPoint & {[SystemColumnName.ROW_ID]: JSONScalar}
> & {
  axisTypes: RegressionAxisTypes & {[SystemColumnName.ROW_ID]: DataType.STRING; x: DataType.NUMBER};
};

export interface RegressionState {
  inputDataset: RegressionDataset;
  outlierConfig: OutlierConfig | null;
  regressionAnalysis: RegressionAnalysis;
  regressionConfig: RegressionConfig;
}

export function assertRegressionDatasetIsNumerical(
  regressionDataset: RegressionDataset
): asserts regressionDataset is NumericRegressionDataset {
  assert(regressionDataset.axisTypes.x === DataType.NUMBER, 'Expected dataset to have a numerical x-axis');
}

export function datasetToRegressionDataset(
  dataset: Dataset<CartesianPoint>,
  axisTypes: RegressionAxisTypes
): RegressionDataset {
  return {
    points: dataset.points,
    axisTypes,
  };
}
